import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, DoorOpen, MapPin, Store } from "lucide-react";
import { toast } from "sonner";
import { useMall } from "@/context/MallContext";
import api from "@/lib/api";
import { tint } from "@/lib/mall";

/** Always on screen: which shop you're standing in, or the hallway itself. */
export default function MallBar() {
    const { store, theme, anchors, enter, leave, solo } = useMall();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const loc = useLocation();

    const walkToDirectory = () => navigate("/directory");

    // On a Virtual Brick & Mortar site there is no hallway to stand in, and the back office
    // has its own bar telling you whose books you're in.
    if (solo) return null;
    if (loc.pathname.startsWith("/account") || loc.pathname.startsWith("/admin")) return null;

    if (!store) {
        return (
            <div className="sticky top-16 z-40 border-b border-white/12 bg-white/[0.04] backdrop-blur-xl" data-testid="mall-hallway-bar">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 sm:px-6">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">You are in</span>
                    <span className="inline-flex items-center gap-1.5 font-display text-sm font-black text-white" data-testid="mall-bar-hallway">
                        <Store size={13} /> the 13thav directory
                    </span>
                    <button onClick={walkToDirectory} data-testid="mall-bar-walk-in"
                        className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-black transition-transform hover:scale-[1.03]">
                        <DoorOpen size={12} /> Pick a shop
                    </button>
                </div>
            </div>
        );
    }

    const others = anchors.filter((a) => a.slug !== store.slug);

    /** Stay in the same aisle — only fall back to their front door if they don't stock it. */
    const walkTo = async (a) => {
        setOpen(false);
        enter(a);
        try {
            const { data } = await api.get(`/stores/${a.slug}/walk`, { params: { path: loc.pathname } });
            if (data?.fallback) toast.info(`${a.name} doesn't stock that one — here's their store.`);
            navigate(data?.path || `/stores/${a.slug}`);
        } catch {
            navigate(`/stores/${a.slug}`);
        }
    };

    return (
        <div className="sticky top-16 z-40 border-b backdrop-blur-xl"
            style={{ background: tint(theme.primary, 0.1), borderColor: tint(theme.primary, 0.35) }}
            data-testid="mall-store-bar">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 sm:px-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">You are in</span>
                <Link to={`/stores/${store.slug}`} className="inline-flex items-center gap-1.5 font-display text-sm font-black"
                    style={{ color: theme.ink || theme.primary }} data-testid="mall-bar-store">
                    <Store size={13} /> {store.name}
                </Link>
                {store.city && (
                    <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                        <MapPin size={10} /> {store.city}
                    </span>
                )}
                {store.fulfillment_line && (
                    <span className="hidden font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400 sm:inline">
                        {store.fulfillment_line}
                    </span>
                )}
                <span className="hidden font-mono text-[11px] uppercase tracking-[0.16em] md:inline"
                    style={{ color: theme.ink || theme.primary }}>{store.tier_label}</span>

                <div className="ml-auto flex items-center gap-2">
                    {others.length > 0 && (
                        <div className="relative">
                            <button onClick={() => setOpen((v) => !v)} data-testid="mall-bar-switch"
                                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-200 transition-colors hover:text-white"
                                style={{ borderColor: tint(theme.primary, 0.45) }}>
                                Switch shop <ChevronDown size={12} />
                            </button>
                            {open && (
                                <div className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-white/12 bg-brand-ink shadow-2xl"
                                    data-testid="mall-bar-switch-menu">
                                    {others.map((a) => (
                                        <button key={a.slug} onClick={() => walkTo(a)} data-testid={`mall-walk-${a.slug}`}
                                            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5">
                                            <span className="h-7 w-1.5 rounded-full" style={{ background: a.theme?.primary }} />
                                            <span className="min-w-0">
                                                <span className="block truncate font-display text-sm font-bold">{a.name}</span>
                                                <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">{a.city}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <button onClick={() => { leave(); navigate("/directory"); }} data-testid="mall-bar-leave"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/20">
                        <DoorOpen size={12} /> Directory
                    </button>
                </div>
            </div>
        </div>
    );
}
